import Vue from "vue";
import VueRouter, { RouteConfig } from "vue-router";
import Home from "../views/Home.vue";
import store from "../store";

Vue.use(VueRouter);

const isMobile = () => {
  if (screen.width <= 760) {
    return true;
  } else {
    return false;
  }
};

const canAccessProductDirectory = () => {
  // До логина доступ разрешаем: App.vue при отсутствии сессии показывает
  // только LoginForm (router-view не монтируется), и после входа страница
  // открывается по исходному URL. Проверка прав — только для вошедших.
  if (!store.getters["ubic/isAuthenticated"]) {
    return true;
  }

  return !!(
    store.getters["ubic/isAdmin"] || store.getters["ubic/isProducts"]
  );
};

const productCatalogEntityBeforeEnter = (
  pageImport: () => Promise<any>,
  proxyImport: () => Promise<any>
) => {
  return (to: any, from: any, next: any) => {
    const twModalView = from.matched.length > 0;

    if (!twModalView || isMobile()) {
      to.matched[0].components = {
        default: pageImport,
        // @ts-ignore
        modal: false
      };
    }

    if (twModalView && !isMobile()) {
      if (from.matched.length > 1) {
        const childrenView = from.matched.slice(1, from.matched.length);

        for (const view of childrenView) {
          to.matched.push(view);
        }
      }

      to.matched[0].components = to.matched[0].components || {};
      to.matched[0].components.default = from.matched[0].components.default;
      to.matched[0].components.modal = proxyImport;
    }

    next();
  };
};

const directoryProductBeforeEnter = (
  pageImport: () => Promise<any>,
  proxyImport: () => Promise<any>
) => {
  const entityBeforeEnter = productCatalogEntityBeforeEnter(
    pageImport,
    proxyImport
  );

  return (to: any, from: any, next: any) => {
    // entity-роут — sibling `/product-directory`, его guard не наследуется
    if (!canAccessProductDirectory()) {
      next({ name: "product-catalog" });
      return;
    }

    entityBeforeEnter(to, from, next);
  };
};

const routes: Array<RouteConfig> = [
  {
    path: "/",
    redirect: { name: "work_plan_board" },
  },

  // ////////////////////////////////////////////////////////////////////////
  // COMMON / PERONAL PART
  // ////////////////////////////////////////////////////////////////////////
  {
    meta: {
      title: "План/Факт",
    },
    path: "/work-plan-board",
    name: "work_plan_board",
    component: () =>
      import(
        /* webpackChunkName: "WorkPlanBoard" */
        "../modules/WorkPlanBoard/WorkPlanBoard.vue"
      ),
  },
  {
    meta: {
      title: "Уведомления",
    },
    path: "/notifications",
    name: "notifications",
    component: () =>
      import(
        /* webpackChunkName: "Notifications" */
        "../modules/Personal/Notifications/Notifications.vue"
      ),
  },

  {
    meta: {
      title: "Календарь",
    },
    path: "/calendar",
    name: "calendar",
    component: () =>
      import(
        /* webpackChunkName: "Calendar" */
        "../modules/Personal/Calendar/Calendar.vue"
      ),
  },

  // ////////////////////////////////////////////////////////////////////////
  // COMPANY PART
  // ////////////////////////////////////////////////////////////////////////
  {
    meta: {
      title: "Сотрудники",
    },
    path: "/employees/:view?",
    name: "employee-list",
    component: () =>
      import(
        /* webpackChunkName: "EmployeeList" */
        "../modules/EmployeeList/EmployeeList.vue"
      ),
  },

  {
    meta: {
      title: "Редактирование сотрудника",
      twModalView: true,
    },
    path: "/employees/details/:id",
    name: "employee-details",
    beforeEnter: (to, from, next) => {
      const twModalView = from.matched.length > 0; //.some(view => view.meta && view.meta.twModalView)

      if (!twModalView) {
        //
        // For direct access
        //
        to.matched[0].components = {
          default: () =>
            import(
              /* webpackChunkName: "EmployeeDetails" */
              "../modules/EmployeeDetails/EmployeeDetails.vue"
            ),
          // @ts-ignore
          modal: false,
        };
      }

      if (twModalView) {
        //
        // For twModalView access
        //
        if (from.matched.length > 1) {
          // copy nested router
          const childrenView = from.matched.slice(1, from.matched.length);

          for (const view of childrenView) {
            to.matched.push(view);
          }
        }

        if (to.matched[0].components) {
          // Rewrite components for `default`
          to.matched[0].components.default = from.matched[0].components.default;
          // Rewrite components for `modal`
          to.matched[0].components.modal = () =>
            import(
              /* webpackChunkName: "EmployeeDetailsProxy" */
              "../modules/EmployeeDetails/EmployeeDetailsProxy.vue"
            );
        }
      }

      next();
    },
  },

  {
    meta: {
      title: "Структура",
    },
    path: "/departments",
    name: "department-manager",
    component: () =>
      import(
        /* webpackChunkName: "DepartmentsManager" */
        "../modules/DepartmentsManager/DepartmentsManager.vue"
      ),
  },

  // ////////////////////////////////////////////////////////////////////////
  // CRM PART
  // ////////////////////////////////////////////////////////////////////////
  {
    meta: {
      title: "Клиенты",
    },
    path: "/clients",
    name: "clients-list",
    component: () =>
      import(
        /* webpackChunkName: "ClientList" */
        "../modules/ClientList/ClientList.vue"
      ),
  },

  {
    meta: {
      title: "Агенты",
    },
    path: "/agents",
    name: "agents-list",
    component: () =>
      import(
        /* webpackChunkName: "AgentList" */
        "../modules/Agents/AgentList/AgentList.vue"
      ),
  },

  {
    meta: {
      title: "Карточка агента",
      twModalView: true,
    },
    path: "/agents/details/:id",
    name: "agent-details",
    beforeEnter: (to, from, next) => {
      const twModalView = from.matched.length > 0;

      if (!twModalView || isMobile()) {
        to.matched[0].components = {
          default: () =>
            import(
              /* webpackChunkName: "AgentDetails" */
              "../modules/Agents/AgentDetails/AgentDetails.vue"
            ),
          // @ts-ignore
          modal: false,
        };
      }

      if (twModalView && !isMobile()) {
        if (from.matched.length > 1) {
          const childrenView = from.matched.slice(1, from.matched.length);

          for (const view of childrenView) {
            to.matched.push(view);
          }
        }

        if (to.matched[0].components) {
          to.matched[0].components.default = from.matched[0].components.default;
          to.matched[0].components.modal = () =>
            import(
              /* webpackChunkName: "AgentDetailsProxy" */
              "../modules/Agents/AgentDetails/AgentDetailsProxy.vue"
            );
        }
      }

      next();
    },
  },

  {
    meta: {
      title: "Редактирование клиента",
      twModalView: true,
    },
    path: "/clients/details/:id",
    name: "client-details",
    beforeEnter: (to, from, next) => {
      const twModalView = from.matched.length > 0; //.some(view => view.meta && view.meta.twModalView)

      if (!twModalView || isMobile()) {
        //
        // For direct access
        //
        to.matched[0].components = {
          default: () =>
            import(
              /* webpackChunkName: "ClientDetails" */
              "../modules/ClientDetails/ClientDetails.vue"
            ),
          // @ts-ignore
          modal: false,
        };
      }

      if (twModalView && !isMobile()) {
        //
        // For twModalView access
        //
        if (from.matched.length > 1) {
          // copy nested router
          const childrenView = from.matched.slice(1, from.matched.length);

          for (const view of childrenView) {
            to.matched.push(view);
          }
        }

        if (to.matched[0].components) {
          // Rewrite components for `default`
          to.matched[0].components.default = from.matched[0].components.default;
          // Rewrite components for `modal`
          to.matched[0].components.modal = () =>
            import(
              /* webpackChunkName: "ClientDetailsProxy" */
              "../modules/ClientDetails/ClientDetailsProxy.vue"
            );
        }
      }

      next();
    },
  },

  {
    meta: {
      title: "Заявки",
    },
    path: "/deals",
    name: "deal-list",
    component: () =>
      import(
        /* webpackChunkName: "DealList" */
        "../modules/DealList/DealList.vue"
      ),
  },

  {
    meta: {
      title: "Прогноз отгрузок",
    },
    path: "/shipment-forecast",
    name: "shipment-forecast",
    component: () =>
      import(
        /* webpackChunkName: "ShipmentForecast" */
        "../modules/ShipmentForecast/ShipmentForecast.vue"
      ),
  },
  {
    meta: {
      title: "Прогноз отгрузок по таре",
    },
    path: "/shipment-forecast-by-container",
    name: "shipment-forecast-by-container",
    component: () =>
      import(
        /* webpackChunkName: "ShipmentForecastByContainer" */
        "../modules/ShipmentForecastByContainer/ShipmentForecastByContainer.vue"
      ),
  },
  {
    meta: {
      title: "Прогноз отгрузок на дату",
    },
    path: "/shipment-forecast-by-date",
    name: "shipment-forecast-by-date",
    component: () =>
      import(
        /* webpackChunkName: "ShipmentForecastByDate" */
        "../modules/ShipmentForecastByDate/ShipmentForecastByDate.vue"
      ),
  },
  {
    meta: {
      title: "Планирование производства",
    },
    path: "/production-forecast-daily",
    name: "production-forecast-daily",
    component: () =>
      import(
        /* webpackChunkName: "ProductionForecastDaily" */
        "../modules/ProductionForecastDaily/ProductionForecastDaily.vue"
      ),
  },
  {
    meta: {
      title: "Микскарт заявки",
    },
    path: "/mixcart",
    name: "mixcart-list",
    component: () =>
      import(
        /* webpackChunkName: "MixcartList" */
        "../modules/MixcartList/MixcartList.vue"
      ),
  },

  {
    meta: {
      title: "Воронки",
    },
    path: "/deal-pipelines/:id",
    name: "deal-pipelines",
    component: () =>
      import(
        /* webpackChunkName: "DealPipelines" */
        "../modules/DealPipelines/DealPipelines.vue"
      ),
  },

  {
    meta: {
      title: "Редактирование заявки",
      twModalView: true,
    },
    path: "/deals/details/:id",
    name: "deal-details",
    component: () =>
      import(
        /* webpackChunkName: "DealDetails" */
        "../modules/DealDetails/DealDetails.vue"
      ),
    // beforeEnter: (to, from, next) => {
    //   const twModalView = from.matched.length > 0 //.some(view => view.meta && view.meta.twModalView)

    //   if (!twModalView|| isMobile()) {
    //     //
    //     // For direct access
    //     //
    //     to.matched[0].components = {
    //       default: () => import(
    //         /* webpackChunkName: "DealDetails" */
    //         '../modules/DealDetails/DealDetails.vue'
    //       ),
    //       // @ts-ignore
    //       modal: false
    //     }
    //   }

    //   if (twModalView && !isMobile()) {
    //     //
    //     // For twModalView access
    //     //
    //     if (from.matched.length > 1) {
    //       // copy nested router
    //       const childrenView = from.matched.slice(1, from.matched.length)

    //       for (const view of childrenView) {
    //         to.matched.push(view)
    //       }
    //     }

    //     if (to.matched[0].components) {
    //       // Rewrite components for `default`
    //       to.matched[0].components.default = from.matched[0].components.default
    //       // Rewrite components for `modal`
    //       to.matched[0].components.modal = () => import(
    //         /* webpackChunkName: "DealDetailsProxy" */
    //         '../modules/DealDetails/DealDetailsProxy.vue'
    //       )
    //     }
    //   }

    //   next()
    // }
  },

  {
    meta: {
      title: "Склады",
    },
    path: "/warehouses",
    name: "warehouses-list",
    component: () =>
      import(
        /* webpackChunkName: "WarehousesList" */
        "../modules/WarehousesList/WarehousesList.vue"
      ),
  },
  {
    meta: {
      title: "Расхождения остатков",
    },
    path: "/warehouses-resort",
    name: "warehouses-resort-list",
    component: () =>
      import(
        /* webpackChunkName: "WarehousesList" */
        "../modules/WarehousesList/WarehouseResortList/WarehouseResortList.vue"
      ),
  },
  {
    meta: {
      title: "Подтвеждение доставки",
    },
    path: "/warehouses-delivery-approve",
    name: "warehouses-delivery-approve-list",
    component: () =>
      import(
        /* webpackChunkName: "WarehousesList" */
        "../modules/WarehousesList/WarehouseDeliveryApproveList/WarehouseDeliveryApproveList.vue"
      ),
  },

  // ////////////////////////////////////////////////////////////////////////
  // FINANCE PART
  // ////////////////////////////////////////////////////////////////////////

  {
    meta: {
      title: "Транспортные расходы",
    },
    path: "/transport-costs",
    name: "transport-costs-list",
    component: () =>
      import(
        /* webpackChunkName: "TransportCostsList" */
        "../modules/TransportCostsList/TransportCostsList.vue"
      ),
  },
  // ////////////////////////////////////////////////////////////////////////
  // DELIVERY PART
  // ////////////////////////////////////////////////////////////////////////
  {
    meta: {
      title: "Маршрутные листы",
    },
    path: "/delivery-sheet",
    name: "delivery-sheet",
    component: () =>
      import(
        /* webpackChunkName: "DeliverySheetBoard" */
        "../modules/DeliverySheetBoard/DeliverySheetBoard.vue"
      ),
  },

  {
    meta: {
      title: "Управление зонами",
    },
    path: "/delivery-zones",
    name: "delivery-zones-manager",
    component: () =>
      import(
        /* webpackChunkName: "DeliveryZonesManager" */
        "../modules/DeliveryZonesManager/DeliveryZonesManager.vue"
      ),
  },

  {
    meta: {
      title: "Управление машинами и водителями",
    },
    path: "/delivery-vehicles",
    name: "delivery-vehicles-manager",
    component: () =>
      import(
        /* webpackChunkName: "DeliveryVehiclesManager" */
        "../modules/DeliveryVehiclesManager/DeliveryVehiclesManager.vue"
      ),
  },

  {
    meta: {
      title: "Управление датами доставки",
    },
    path: "/delivery-dates",
    name: "delivery-dates-manager",
    component: () =>
      import(
        /* webpackChunkName: "DeliveryDatesManager" */
        "../modules/DeliveryDatesManager/DeliveryDatesManager.vue"
      ),
  },

  // ////////////////////////////////////////////////////////////////////////
  // PLANING PART
  // ////////////////////////////////////////////////////////////////////////
  {
    meta: {
      title: "Планирование",
    },
    path: "/planing/:view",
    name: "planing",
    component: () =>
      import(
        /* webpackChunkName: "Planing" */
        "../modules/Planing/Planing.vue"
      ),
  },

  // ////////////////////////////////////////////////////////////////////////
  // REPORTS PART
  // ////////////////////////////////////////////////////////////////////////
  {
    meta: {
      title: "Отчеты: По маржинальности",
    },
    path: "/reports/marginality",
    name: "reports-marginality",
    component: () =>
      import(
        /* webpackChunkName: "MarginalityReport" */
        "../modules/Reports/MarginalityReport/MarginalityReport.vue"
      ),
  },
  {
    meta: {
      title: "Отчеты: По аналитике по годам и продуктам",
    },
    path: "/reports/year-product",
    name: "reports-year-product",
    component: () =>
      import(
        /* webpackChunkName: "YearProductReport" */
        "../modules/Reports/YearProductReport/YearProductReport.vue"
      ),
  },
  {
    meta: {
      title: "Отчеты: Потребность в полуфабрикате",
    },
    path: "/reports/semifinished-requirement",
    name: "reports-semifinished-requirement",
    component: () =>
      import(
        /* webpackChunkName: "SemifinishedRequirementReport" */
        "../modules/Reports/SemifinishedRequirementReport/SemifinishedRequirementReport.vue"
      ),
  },
  {
    meta: {
      title: "Отчеты: По изменению себестоимости",
    },
    path: "/reports/prime-costs",
    name: "prime-costs-report",
    component: () =>
      import(
        /* webpackChunkName: "PrimeCostsReport" */
        "../modules/Reports/PrimeCostsReport/PrimeCostsReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: По маржинальности продуктов",
    },
    path: "/reports/marginality-by-product",
    name: "reports-marginality-by-product",
    component: () =>
      import(
        /* webpackChunkName: "MarginalityByProductReport" */
        "../modules/Reports/MarginalityByProductReport/MarginalityByProductReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: По маржинальности продуктов",
    },
    path: "/reports/marginality-by-product-detail",
    name: "reports-marginality-by-product-detail",
    component: () =>
      import(
        /* webpackChunkName: "MarginalityByProductDetailReport" */
        "../modules/Reports/MarginalityByProductDetailReport/MarginalityByProductDetailReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: по траспортным/клиентам",
    },
    path: "/reports/transports_costs_by_clients",
    name: "reports-transports_costs_by_clients",
    component: () =>
      import(
        /* webpackChunkName: "TransportCostsByClientReport" */
        "../modules/Reports/TransportCostsByClientReport/TransportCostsByClientReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: Анализ точек доставки",
    },
    path: "/reports/analize-delivery-points",
    name: "reports-analize-delivery-points",
    component: () =>
      import(
        /* webpackChunkName: "AnalizeDeliveryPoints" */
        "../modules/Reports/AnalizeDeliveryPoints/AnalizeDeliveryPoints.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: По водителям",
    },
    path: "/reports/by-driver-report",
    name: "reports-by-driver",
    component: () =>
      import(
        /* webpackChunkName: "ByDriverReport" */
        "../modules/Reports/ByDriverReport/ByDriverReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: Заявки",
    },
    path: "/reports/deals-report",
    name: "reports-deals",
    component: () =>
      import(
        /* webpackChunkName: "DealReports" */
        "../modules/Reports/DealReports/DealReports.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: Продукты",
    },
    path: "/reports/products-report",
    name: "reports-products",
    component: () =>
      import(
        /* webpackChunkName: "ProductsReport" */
        "../modules/Reports/ProductsReport/ProductsReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: Перемещения на склады",
    },
    path: "/reports/warehouse-movement",
    name: "reports-warehouse-movement",
    component: () =>
      import(
        /* webpackChunkName: "WarehouseMovementReport" */
        "../modules/Reports/WarehouseMovementReport/WarehouseMovementReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчеты: Отгрузка со складов",
    },
    path: "/reports/warehouses-clients",
    name: "reports-warehouse-clients",
    component: () =>
      import(
        /* webpackChunkName: "WarehouseClientsReport" */
        "../modules/Reports/WarehouseClientsReport/WarehouseClientsReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по планированию",
    },
    path: "/reports/planing/:view",
    name: "reports-planing",
    component: () =>
      import(
        /* webpackChunkName: "PlaningReport" */
        "../modules/Reports/PlaningReport/PlaningReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по активности",
    },
    path: "/reports/activity",
    name: "reports-activity",
    component: () =>
      import(
        /* webpackChunkName: "Activity" */
        "../modules/Reports/Activity/Activity.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по рекламации",
    },
    path: "/reports/complaints",
    name: "reports-complaints",
    component: () =>
      import(
        /* webpackChunkName: "ComplaintsReport" */
        "../modules/Reports/ComplaintsReport/ComplaintsReport.vue"
      ),
  },
  {
    meta: {
      title: "Отчет по рекламации (расширенный)",
    },
    path: "/reports/complaints-extend",
    name: "reports-complaints-extend",
    component: () =>
      import(
        /* webpackChunkName: "ComplaintsExtendReport" */
        "../modules/Reports/ComplaintsExtendReport/ComplaintsExtendReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по некондиции",
    },
    path: "/reports/substandard",
    name: "reports-substandard",
    component: () =>
      import(
        /* webpackChunkName: "WarehouseSubstandardReport" */
        "../modules/Reports/WarehouseSubstandardReport/WarehouseSubstandardReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по транспортным расходам",
    },
    path: "/reports/transport-costs",
    name: "transport-costs",
    component: () =>
      import(
        /* webpackChunkName: "TransportCostsReport" */
        "../modules/Reports/TransportCostsReport/TransportCostsReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по транспортным затратам",
    },
    path: "/reports/transport-expences",
    name: "transport-expences",
    component: () =>
      import(
        /* webpackChunkName: "TransportExpencesReport" */
        "../modules/Reports/TransportExpencesReport/TransportExpencesReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по остаткам на складе",
    },
    path: "/reports/warehouse-by-date",
    name: "warehouse-by-date",
    component: () =>
      import(
        /* webpackChunkName: "WarehouseByDateReport" */
        "../modules/Reports/WarehouseByDateReport/WarehouseByDateReport.vue"
      ),
  },

  {
    meta: {
      title: "Отчет по движению товаров",
    },
    path: "/reports/warehouse-by-transactions",
    name: "warehouse-by-transactions",
    component: () =>
      import(
        /* webpackChunkName: "WarehouseByTransactionsReport" */
        "../modules/Reports/WarehouseByTransactionsReport/WarehouseByTransactionsReport.vue"
      ),
  },

  {
    meta: {
      title: "Справочник продуктов",
    },
    path: "/product-directory",
    name: "product-directory",
    beforeEnter: (_to, _from, next) => {
      if (canAccessProductDirectory()) {
        next();
        return;
      }
      next({ name: "product-catalog" });
    },
    component: () =>
      import(
        /* webpackChunkName: "ProductDirectory" */
        "../modules/ProductDirectory/ProductDirectory.vue"
      ),
  },
  {
    meta: {
      title: "Продукт",
      twModalView: true,
    },
    path: "/product-directory/product/:id",
    name: "product-directory-product",
    beforeEnter: directoryProductBeforeEnter(
      () =>
        import(
          /* webpackChunkName: "ProductDirectoryEntityPage" */
          "../modules/ProductDirectory/ProductDirectoryEntityPage.vue"
        ),
      () =>
        import(
          /* webpackChunkName: "ProductDirectoryEntityProxy" */
          "../modules/ProductDirectory/ProductDirectoryEntityProxy.vue"
        )
    ),
  },
  {
    meta: {
      title: "Каталог продуктов",
    },
    path: "/product-catalog",
    name: "product-catalog",
    component: () =>
      import(
        /* webpackChunkName: "ProductCatalog" */
        "../modules/ProductCatalog/ProductCatalog.vue"
      ),
  },
  {
    meta: {
      title: "Тип продукта",
      twModalView: true,
      catalogEntity: "type",
    },
    path: "/product-catalog/type/:id",
    name: "product-catalog-type",
    beforeEnter: productCatalogEntityBeforeEnter(
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityPage" */
          "../modules/ProductCatalog/ProductCatalogEntityPage.vue"
        ),
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityProxy" */
          "../modules/ProductCatalog/ProductCatalogEntityProxy.vue"
        )
    ),
  },
  {
    meta: {
      title: "Родительский продукт",
      twModalView: true,
      catalogEntity: "parent",
    },
    path: "/product-catalog/parent/:id",
    name: "product-catalog-parent",
    beforeEnter: productCatalogEntityBeforeEnter(
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityPage" */
          "../modules/ProductCatalog/ProductCatalogEntityPage.vue"
        ),
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityProxy" */
          "../modules/ProductCatalog/ProductCatalogEntityProxy.vue"
        )
    ),
  },
  {
    meta: {
      title: "Продукт",
      twModalView: true,
      catalogEntity: "product",
    },
    path: "/product-catalog/product/:id",
    name: "product-catalog-product",
    beforeEnter: productCatalogEntityBeforeEnter(
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityPage" */
          "../modules/ProductCatalog/ProductCatalogEntityPage.vue"
        ),
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityProxy" */
          "../modules/ProductCatalog/ProductCatalogEntityProxy.vue"
        )
    ),
  },
  {
    meta: {
      title: "Тара",
      twModalView: true,
      catalogEntity: "container",
    },
    path: "/product-catalog/container/:id",
    name: "product-catalog-container",
    beforeEnter: productCatalogEntityBeforeEnter(
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityPage" */
          "../modules/ProductCatalog/ProductCatalogEntityPage.vue"
        ),
      () =>
        import(
          /* webpackChunkName: "ProductCatalogEntityProxy" */
          "../modules/ProductCatalog/ProductCatalogEntityProxy.vue"
        )
    ),
  },
  {
    meta: {
      title: "Каталог продуктов: Себестоимость",
    },
    path: "/prime-costs",
    name: "prime-costs-list",
    component: () =>
      import(
        /* webpackChunkName: "PrimeCostsList" */
        "../modules/PrimeCostsList/PrimeCostsList.vue"
      ),
  },

  // ////////////////////////////////////////////////////////////////////////
  // VOCS PART
  // ////////////////////////////////////////////////////////////////////////

  {
    meta: {
      title: "Словарь: Города",
    },
    path: "/vocs/cities",
    name: "vocs-cities",
    component: () =>
      import(
        /* webpackChunkName: "VocabulariesCitiesManager" */
        "../modules/Vocabularies/CitiesManager/CitiesManager.vue"
      ),
  },

  {
    meta: {
      title: "Словарь: Должности",
    },
    path: "/vocs/positions",
    name: "vocs-positions",
    component: () =>
      import(
        /* webpackChunkName: "VocabulariesPositionsManager" */
        "../modules/Vocabularies/PositionsManager/PositionsManager.vue"
      ),
  },

  {
    meta: {
      title: "Словарь: Типы задач",
    },
    path: "/vocs/todo-types",
    name: "vocs-todo-types",
    component: () =>
      import(
        /* webpackChunkName: "VocabulariesTodoTypesManager" */
        "../modules/Vocabularies/TodoTypesManager/TodoTypesManager.vue"
      ),
  },
  {
    meta: {
      title: "Словарь: Склады",
    },
    path: "/vocs/warehouses",
    name: "warehouses-manager",
    component: () =>
      import(
        /* webpackChunkName: "WarehouseManager" */
        "../modules/Vocabularies/WarehouseManager/WarehouseManager.vue"
      ),
  },
  {
    meta: {
      title: "Словарь: Стикеры",
      table: "voc_stickers",
      name: "Стикеры",
      ignore_fields: ["quarantine", "recycling", "color"],
    },
    path: "/vocs/stickers",
    name: "stickers-manager",
    component: () =>
      import(
        /* webpackChunkName: "VocabularyManager" */
        "../modules/Vocabularies/VocabularyManager/VocabularyManager.vue"
      ),
  },
  {
    meta: {
      title: "Словарь: Типы бесплатных продуктов",
      table: "free_product_types",
      name: "Типы бесплатных продуктов",
      ignore_fields: ["quarantine", "recycling", "color"],
    },
    path: "/vocs/free-product-types",
    name: "free-product-types",
    component: () =>
      import(
        /* webpackChunkName: "VocabularyManager" */
        "../modules/Vocabularies/VocabularyManager/VocabularyManager.vue"
      ),
  },
  {
    meta: {
      title: "Продукты: ценовые сегменты",
      table: "product_price_segments",
      name: "Ценовые сегменты",
      ignore_fields: ["quarantine", "recycling"]
    },
    path: "/products/product-price-segments",
    name: "product-price-segments-manager",
    component: () =>
      import(
        /* webpackChunkName: "VocabularyManager" */
        "../modules/Vocabularies/VocabularyManager/VocabularyManager.vue"
      )
  },
  {
    meta: {
      title: "Продукты: технологии",
      table: "product_technologies",
      name: "Технологии",
      ignore_fields: ["quarantine", "recycling"]
    },
    path: "/products/product-technologies",
    name: "product-technologies-manager",
    component: () =>
      import(
        /* webpackChunkName: "VocabularyManager" */
        "../modules/Vocabularies/VocabularyManager/VocabularyManager.vue"
      )
  },
  {
    meta: {
      title: "Продукты: торговые марки",
      table: "product_trade_marks",
      name: "Ценовые сегменты",
      ignore_fields: ["quarantine", "recycling", "color"]
    },
    path: "/products/product-trade-marks",
    name: "product-trade-marks-manager",
    component: () =>
      import(
        /* webpackChunkName: "VocabularyManager" */
        "../modules/Vocabularies/VocabularyManager/VocabularyManager.vue"
      )
  },
  {
    meta: {
      title: "Словарь: НДС",
      table: "vat_rates",
      name: "НДС",
      ignore_fields: ["quarantine", "recycling", "color"],
      value_field: { field: "rate", label: "Ставка, %" }
    },
    path: "/vocs/vat-rates",
    name: "vat-rates-manager",
    component: () =>
      import(
        /* webpackChunkName: "VocabularyManager" */
        "../modules/Vocabularies/VocabularyManager/VocabularyManager.vue"
      )
  },
  {
    meta: {
      title: "Справочник: Юрлица",
    },
    path: "/vocs/entities",
    name: "entities-manager",
    component: () =>
      import(
        /* webpackChunkName: "EntitiesManager" */
        "../modules/Vocabularies/EntitiesManager/EntitiesManager.vue"
      )
  },

  {
    meta: {
      title: "Синхронизация БД",
    },
    path: "/db-sync",
    name: "db-sync",
    component: () =>
      import(
        /* webpackChunkName: "DbSyncManager" */
        "../modules/DbSyncManager/DbSyncManager.vue"
      ),
  },

  {
    meta: {
      title: "Синхронизация БД",
    },
    path: "/db-sync",
    name: "db-sync",
    component: () =>
      import(
        /* webpackChunkName: "DbSyncManager" */
        "../modules/DbSyncManager/DbSyncManager.vue"
      ),
  },

  {
    meta: {
      title: "Инструменты",
    },
    path: "/utils",
    name: "utils",
    component: () =>
      import(
        /* webpackChunkName: "Utils" */
        "../modules/Utils/Utils.vue"
      ),
  },

  {
    meta: {
      title: "Настройки",
    },
    path: "/settings",
    name: "settings",
    component: () =>
      import(
        /* webpackChunkName: "Settings" */
        "../modules/Settings/Settings.vue"
      ),
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  linkActiveClass: "is-active",
  routes,
});

router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.title) {
    window.document.title = `${to.meta.title} | Русбиомикс: система управления`;
    if (to.meta.name) {
      to.params.name = to.meta.name;
      to.params.table = to.meta.table;
      to.params.ignore_fields = to.meta.ignore_fields;
      to.params.value_field = to.meta.value_field;
    }
  }

  store.commit("hideSubmenu");

  next();
});

export default router;
